import { UploadOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn, useRequest, useToggle } from 'ahooks';
import { Button, Form, Input, message, Modal, Select, Upload } from 'antd';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import { useAuthorization } from 'components/Authorization';
import StatusButton from 'components/StatusButton';
import { useCustomUpload } from 'hooks/business/useUploadResource';
import { useMemo, useState } from 'react';
import { returnReviewReject } from 'services/api/return';
import { platformUploadSizeLimitMap } from 'services/config/platform';
import { ReturnActionMercadoProps } from './types';

export const ReturnReviewReject = (props: ReturnActionMercadoProps) => {
  const { returnSn, internalStoreId } = props;
  const { t } = useTranslate();
  const [visible, { toggle: toggleVisible }] = useToggle();
  const [isUploadRequired, setIsUploadRequired] = useState(false);
  const customUpload = useCustomUpload();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { token } = useAuthorization();

  const [form] = Form.useForm();

  const uploadAccept = 'jpg,jpeg,bmp,pdf,png';
  const reasonOptions = useMemo(
    () => [
      {
        label: t('domain.returnRefund.mercadoReviewReject.reason.arrivedDamaged'),
        value: 'SRF2',
      },
      {
        label: t('domain.returnRefund.mercadoReviewReject.reason.incompleteReturn'),
        value: 'SRF3',
      },
      {
        label: t('domain.returnRefund.mercadoReviewReject.reason.differentProduct'),
        value: 'SRF4',
      },
      {
        label: t('domain.returnRefund.mercadoReviewReject.reason.productNotInPackage'),
        value: 'SRF5',
      },
      {
        label: t('domain.returnRefund.mercadoReviewReject.reason.otherDefect'),
        value: 'SRF6',
      },
      {
        label: t('domain.returnRefund.mercadoReviewReject.reason.packageNotDelivered'),
        value: 'SRF7',
      },
    ],
    [t],
  );

  const { loading, runAsync: submitForm } = useRequest(
    (values) =>
      returnReviewReject({
        platform: 'MERCADO',
        returnSn,
        reasonId: values.reasonId,
        comment: values.comment,
        image: values.image,
      }),
    {
      manual: true,
      onSuccess() {
        toggleVisible();
        message.success(t('common.feedback.submitSuccess'));
      },
      onError() {
        message.error(t('common.feedback.operationFailedPleaseRetry'));
      },
    },
  );

  const handleChange = (info: UploadChangeParam<any>) => {
    const { fileList } = info;
    setFileList(fileList);
    const fList: UploadFile[] = [];
    fileList.forEach((v) => {
      if (v.error) {
        message.error(`${t('common.feedback.uploadFailed')}: ${v.name}`);
      }
      if (v.error || !v.status) {
        return;
      }
      let { url } = v;
      if (v.response?.code === 200) {
        url = v.response?.data?.url;
      }
      fList.push({
        uid: v.uid,
        name: v.name,
        status: v.status,
        url,
      });
    });
    if (fList.length !== fileList.length) {
      setFileList(fList);
    }
  };

  const handleBeforeUpload = useMemoizedFn((file: RcFile) => {
    const uploadSizeLimit = platformUploadSizeLimitMap['mercado'];

    if (uploadAccept.split(',').every((value) => !file.type.includes(value))) {
      message.warning(
        t('common.validation.supportedFileFormats', {
          type: uploadAccept,
        }),
      );
      return Upload.LIST_IGNORE;
    }
    if (file.size > uploadSizeLimit) {
      message.warn(
        t('common.validation.fileSizeLimit', {
          size: `${(uploadSizeLimit / 1024 / 1024).toFixed(2)}M`,
        }),
      );
      return Upload.LIST_IGNORE;
    }
  });

  return (
    <>
      <StatusButton
        size="small"
        onClick={async () => {
          toggleVisible();
          return Promise.resolve();
        }}
      >
        {t('scene.orderWorkspace.returnRefundTab.status.inspectionFailed')}
      </StatusButton>
      <Modal
        visible={visible}
        title={t('scene.orderWorkspace.returnRefundTab.status.inspectionFailed')}
        okText={t('common.action.submit')}
        onOk={() => {
          form.submit();
        }}
        okButtonProps={{ loading }}
        onCancel={toggleVisible}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, values) => {
            // 上传证据必填
            setIsUploadRequired(['SRF2', 'SRF4'].includes(values.reasonId));
          }}
          onFinish={(values) => {
            const image = fileList.map((v) => v?.response?.data?.url);
            submitForm({
              ...values,
              image,
            });
          }}
        >
          <Form.Item
            label={t('scene.orderWorkspace.returnRefundTab.action.selectReason')}
            name="reasonId"
            required
            rules={[
              {
                required: true,
                message: t('scene.orderWorkspace.returnRefundTab.validation.pleaseSelectReason'),
                whitespace: true,
              },
            ]}
          >
            <Select
              placeholder={t('scene.orderWorkspace.returnRefundTab.validation.pleaseSelectReason')}
              options={reasonOptions}
            />
          </Form.Item>
          <Form.Item
            label={t('scene.orderWorkspace.returnRefundTab.detail.field.description')}
            name="comment"
            required
            rules={[
              {
                type: 'string',
                required: true,
                message: t(
                  'scene.orderWorkspace.returnRefundTab.detail.placeholder.enterDescription',
                ),
                whitespace: true,
              },
            ]}
          >
            <Input.TextArea
              placeholder={t(
                'scene.orderWorkspace.returnRefundTab.detail.placeholder.enterDescription',
              )}
              showCount
              maxLength={320}
              autoSize={{ minRows: 4, maxRows: 6 }}
            />
          </Form.Item>
          <Form.Item
            label={t('scene.orderWorkspace.returnRefundTab.list.action.uploadEvidence')}
            name="image"
            required={isUploadRequired}
            rules={[
              {
                required: isUploadRequired,
                validator: (_, value) => {
                  if (!isUploadRequired) return Promise.resolve();

                  if (!value || value?.length === 0) {
                    return Promise.reject(t('common.validation.pleaseUploadFile'));
                  }
                  return Promise.resolve();
                },
                validateTrigger: ['onSubmit'],
              },
            ]}
          >
            <Upload
              multiple
              headers={{
                [import.meta.env.VITE_AUTH_HEADER as string]: `Bearer ${token}`,
              }}
              accept={uploadAccept}
              beforeUpload={handleBeforeUpload}
              fileList={fileList}
              onChange={handleChange}
              customRequest={(options) =>
                customUpload(options, {
                  storeId: internalStoreId,
                  fileType: 'IMAGE',
                  reqSource: 'ATTACHMENT',
                })
              }
            >
              <Button icon={<UploadOutlined />}>
                {t('scene.orderWorkspace.returnRefundTab.list.action.uploadEvidence')}
              </Button>
              <p className="text-content-200 !mt-2">
                {t('common.validation.fileSizeLimit', {
                  size: 25,
                  type: uploadAccept,
                })}
              </p>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
